// @ts-nocheck
import { NamedMonthlyExpenseComparisonItemModel } from "@store/Library/Widgets/Reports/MonthlyExpenseSearchForm";
/* eslint-disable no-restricted-globals */
type SortType = {
    [key: string]: string;
};
const readableMonthsByNumber = {
    "01": "Jan",
    "02": "Feb",
    "03": "Mar",
    "04": "Apr",
    "05": "May",
    "06": "Jun",
    "07": "Jul",
    "08": "Aug",
    "09": "Sep",
    "10": "Oct",
    "11": "Nov",
    "12": "Dec",
};
export const sortApiReturnTable = <T extends SortType[], K extends keyof T[number] & string>(
    data: T,
    sortField: K,
    lastSort: string,
    setLastSort: (sort: string) => void,
) => {
    if (data) {
        const dataCopy = [...data] as T;
        if (lastSort.includes(sortField)) {
            dataCopy.reverse();
            if (lastSort.includes("desc")) {
                setLastSort(sortField);
            } else {
                setLastSort(`${sortField} desc`);
            }
            return dataCopy;
        }
        if (dataCopy) {
            dataCopy.sort((a, b) => {
                if (sortField in a && sortField in b) {
                    if (a[sortField]! < b[sortField]!) {
                        return -1;
                    }
                    if (a[sortField]! > b[sortField]!) {
                        return 1;
                    }
                    return 0;
                }
                // some holes in data. returning -1 to make sure an empty key is sorted at the beginning
                if (sortField in a) {
                    return 1;
                }
                return -1;
            });

            setLastSort(sortField);
            return dataCopy;
        }
    }
};
export const sortDataWithoutState = <T extends SortType[], K extends keyof T[number] & string>(
    data: T,
    sortField: K,
    lastSort: string,
): T | undefined => {
    if (data) {
        const dataCopy = [...data] as T;

        if (dataCopy) {
            dataCopy.sort((a, b) => {
                if (sortField in a && sortField in b) {
                    if (a[sortField]! < b[sortField]!) {
                        return -1;
                    }
                    if (a[sortField]! > b[sortField]!) {
                        return 1;
                    }
                    return 0;
                }
                // some holes in data. returning -1 to make sure an empty key is sorted at the beginning
                if (sortField in a) {
                    return 1;
                }
                return -1;
            });

            if (lastSort.includes("desc")) {
                dataCopy.reverse();
            }

            return dataCopy;
        }
    }
};
export const fixDateTime = (date: string | number) => {
    if (isNaN(Number(date))) {
        const newDate = new Date(date);

        if (newDate.toString() === "Invalid Date") {
            return date;
        }
        return newDate;
    }

    return date;
};

const getDateStringWithoutTime = (date: Date) => {
    return date.toISOString().split("T")[0];
};

export const getTodayDateStringWithoutTime = () => {
    return getDateStringWithoutTime(new Date());
};

export const getOneMonthAgoDateStringWithoutTime = () => {
    const oneMonthAgo = new Date(new Date().setMonth(new Date().getMonth() - 1));
    return getDateStringWithoutTime(oneMonthAgo);
};
export const fixDate = (date: string | number) => {
    const regexForDate = new RegExp(/\d{4}-[01]\d-[0-3]\dT[0-2]\d:[0-5]\d:[0-5]\d(?:\.\d+)?Z?/gm);
    if (String(date).match(regexForDate)) {
        return new Date(date).toLocaleDateString();
    }
    return date;
};
export const formatDate = (date: string) => {
    return new Intl.DateTimeFormat("default", {
        year: "numeric",
        month: "short",
        day: "2-digit",
    })
        .format(new Date(date))
        .toLocaleUpperCase();
};

export const createTableHeaders = (firstDate: number, lastDate: number) => {
    let currentDate: number = firstDate;
    const headerKeys: (string | number)[] = ["name", "itemdesc", "unitofmeasure"];
    const dataHeaders = ["Sku#", "Description", "UM"];

    while (currentDate < lastDate) {
        const currentYear = +String(currentDate).slice(0, 4);
        const currentMonth = String(currentDate).slice(-2);
        headerKeys.push(String(currentDate));
        // @ts-ignore
        dataHeaders.push(`${readableMonthsByNumber[currentMonth]} ${String(currentYear).slice(-2)}`);

        if (Number(currentMonth) === 12) {
            currentDate = +`${currentYear + 1}01`;
        } else {
            currentDate++;
        }
    }

    const currentYear = +String(currentDate).slice(0, 4);
    const currentMonth = String(currentDate).slice(-2);
    // @ts-ignore
    dataHeaders.push(`${readableMonthsByNumber[currentMonth]} ${String(currentYear).slice(-2)}`);
    headerKeys.push(String(currentDate));
    return { headerKeys, dataHeaders };
};
export interface TotalCostData {
    name: string;
    itemdesc: string;
    unitofmeasure: string;
    [key: string]: number | string | JSX.Element | undefined;
}
export const createTableData = (items: NamedMonthlyExpenseComparisonItemModel[]) => {
    let firstMonth = 999999;
    let lastMonth = 0;
    const totCostRows: TotalCostData[] = [];
    const quantityRows: TotalCostData[] = [];
    for (const item of items) {
        const currentItemTotCost = {} as TotalCostData;
        const indexOfItem = totCostRows.findIndex(row => row.name === item.name);
        if (indexOfItem === -1) {
            currentItemTotCost.itemdesc = item.description;
            currentItemTotCost.name = item.name;
            currentItemTotCost.unitofmeasure = item.unitofmeasure;
            currentItemTotCost.shipToName = item.shipToName;
            currentItemTotCost.shipToNumber = item.shipToNumber.replace(")", "");
            const currentItemQuantCost = { ...currentItemTotCost };
            item.quantityAndCostByDate.forEach(item => {
                currentItemTotCost[item.monthYear] = item.cost;
                currentItemQuantCost[item.monthYear] = item.quantity;
                if (Number(item.monthYear) < firstMonth) {
                    firstMonth = +item.monthYear;
                }
                if (Number(item.monthYear) > lastMonth) {
                    lastMonth = +item.monthYear;
                }
            });
            totCostRows.push(currentItemTotCost);
            quantityRows.push(currentItemQuantCost);
        } else {
            item.quantityAndCostByDate.forEach(item => {
                totCostRows[indexOfItem][item.monthYear] =
                    item.cost + Number(totCostRows[indexOfItem][item.monthYear] || 0);
                quantityRows[indexOfItem][item.monthYear] =
                    item.quantity + Number(quantityRows[indexOfItem][item.monthYear] || 0);
                if (Number(item.monthYear) < firstMonth) {
                    firstMonth = Number(item.monthYear);
                }
                if (Number(item.monthYear) > lastMonth) {
                    lastMonth = Number(item.monthYear);
                }
            });
        }
    }
    const { headerKeys, dataHeaders } = createTableHeaders(firstMonth, lastMonth);

    totCostRows.forEach(row => {
        headerKeys.forEach(key => {
            if (!(key in row)) {
                row[key] = "";
            }
        });
    });
    quantityRows.forEach(row => {
        headerKeys.forEach(key => {
            if (!(key in row)) {
                row[key] = "";
            }
        });
    });

    return { totCostRows, quantityRows, headerKeys, dataHeaders };
};
